import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
} from "antd";
import { createSchemaFieldRule } from "antd-zod";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { z } from "zod";

import { BusinessType, CompanyType } from "@/v1/types";
import { FIRM_LABELS, FIRMS } from "@/modules/firms";
import { useAuth } from "@/modules/auth";
import { getCountryCodes, COUNTRY_NAMES } from "@/v1/constants/countryNames";
import {
  createUserRequestDto,
  SupportedLanguage,
} from "@/v1/types/CreateUser.type";
import { getSteuerIdValidatorRule } from "@/v1/utils/steuerId";
import { Footer } from "./styles";
import { AddressAutocomplete, Address } from "./AddressAutocomplete";
import { GoogleMapsApiView } from "./GoogleMapsApiView";

export type CreateUserFormValues = z.infer<typeof createUserRequestDto>;

interface Props {
  onSubmit: (values: CreateUserFormValues) => Promise<void>;
  onReset: () => void;
}

const { Option } = Select;

export const CreateUserForm: React.FC<Props> = ({ onSubmit, onReset }) => {
  const [form] = Form.useForm<CreateUserFormValues>();
  const { t, i18n } = useTranslation("CreateUser");
  const rule = createSchemaFieldRule(createUserRequestDto);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const countryLang = i18n.language.startsWith("de") ? "de" : "en";
  const { getOperatorFirm } = useAuth();

  const handleAddressSelect = (address: Address) => {
    form.setFieldsValue({
      user: {
        ...form.getFieldValue("user"),
        ...address,
      },
    });
  };

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Validate the inputs with Zod schema
      const values = createUserRequestDto.parse(form.getFieldsValue());

      // Submit the form
      await onSubmit(values);

      // Reset the form
      form.resetFields();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      name="add_customer_form"
      disabled={isSubmitting}
      onFinish={handleSubmit}
      onReset={handleReset}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "firstName"]}
            label={t("form.firstName")}
            rules={[rule]}
            required
          >
            <Input autoFocus />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "lastName"]}
            label={t("form.lastName")}
            rules={[rule]}
            required
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "preferredName"]}
            label={t("form.preferredName")}
            rules={[rule]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "businessTradingName"]}
            label={t("form.businessTradingName")}
            rules={[rule]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "companyType"]}
            label={t("form.companyType")}
            rules={[rule]}
          >
            <Select>
              {Object.entries(CompanyType).map(([key, value]) => (
                <Option key={key} value={value}>
                  {value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "firm"]}
            label={t("form.firm")}
            initialValue={getOperatorFirm()}
            rules={[rule]}
            required
          >
            <Select
              options={FIRMS.map((firm) => ({
                value: firm,
                label: FIRM_LABELS[firm],
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "email"]}
            label={t("form.email")}
            rules={[rule]}
            required
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "mobileNumber"]}
            label={t("form.mobileNumber")}
            rules={[rule]}
          >
            <Input placeholder="eg. +4915112345678" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "birthDate"]}
            label={t("form.birthDate")}
            rules={[rule]}
          >
            <DatePicker
              style={{ width: "100%" }}
              format="DD.MM.YYYY"
              disabledDate={(current) => current && current > moment()}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "language"]}
            label={t("form.language.label")}
            initialValue={SupportedLanguage.DE}
            rules={[rule]}
          >
            <Select>
              {Object.entries(SupportedLanguage).map(([key, value]) => (
                <Option key={key} value={value}>
                  {t(`form.language.options.${key}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider plain>{t("form.address")}</Divider>
      <Form.Item>
        <GoogleMapsApiView>
          <AddressAutocomplete onSelect={handleAddressSelect} />
        </GoogleMapsApiView>
      </Form.Item>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "street"]}
            rules={[rule]}
            label={t("form.street")}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={["user", "postCode"]}
            rules={[rule]}
            label={t("form.postCode")}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name={["user", "city"]}
            rules={[rule]}
            label={t("form.city")}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["user", "country"]}
            label={t("form.country")}
            initialValue="DE"
            rules={[rule]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={getCountryCodes().map((code) => ({
                value: code,
                label: COUNTRY_NAMES[code]?.[countryLang] ?? code,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "deTaxId"]}
            label={t("form.deTaxId")}
            // Not the zod rule: antd-zod shows the schema's English message
            // verbatim, so this field carries the translated one instead.
            // The schema still validates on submit.
            rules={[getSteuerIdValidatorRule(t)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Divider />

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name={["kontaxUser", "businessType"]}
            label={t("form.businessType")}
            rules={[rule]}
          >
            <Select
              options={Object.values(BusinessType).map((label) => ({
                value: label,
                label,
              }))}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={["user", "businessTypeComment"]}
            label={t("form.businessTypeComment")}
            rules={[rule]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Footer>
        <Button htmlType="reset" disabled={isSubmitting}>
          {t("button.cancel")}
        </Button>
        <Button type="primary" htmlType="submit" loading={isSubmitting}>
          {t("button.submit")}
        </Button>
      </Footer>
    </Form>
  );
};
